import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import type {
  CandidateDecision,
  CandidateDetailData,
  CandidateQueuePosition,
  PendingDecisionAction,
} from '@/modules/candidate-detail/types';
import type { CandidateStatusApi } from '@/modules/candidates/types';
import { PrescreenLinkSharedBadge } from '@/modules/hiring-manager/components/PrescreenLinkSharedBadge';
import { getInitials } from '@/utils/getInitials';

import { CandidateDecisionArea } from './CandidateDecisionArea';
import { CandidateDraftReview } from './CandidateDraftReview';

interface CandidateHeaderProps {
  candidate: CandidateDetailData;
  /** "Senior Product Designer · Productboard", from experience when loaded. */
  currentRole: string;
  decision: CandidateDecision;
  jobId: string;
  pendingAction: PendingDecisionAction;
  queue?: CandidateQueuePosition;
  searchSuffix: string;
  onReject: (rejectionMessage: string | null) => void;
  status: CandidateStatusApi | null;
}

/**
 * Back link to the job and the decision queue on the first line; name and
 * the decision on the second, where the customer lands.
 */
export function CandidateHeader({
  candidate,
  currentRole,
  decision,
  jobId,
  pendingAction,
  queue,
  searchSuffix,
  onReject,
  status,
}: CandidateHeaderProps) {
  const candidateLink = (candidateId: string) =>
    `/hm/jobs/${jobId}/candidates/${candidateId}${searchSuffix}`;

  return (
    <header className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <Link
          className="hover:text-foreground inline-flex items-center gap-1.5 text-[13px] text-neutral-500"
          to={`/hm/jobs/${jobId}/candidates${searchSuffix}`}
        >
          <ArrowLeft className="size-3.5" />
          {candidate.matchedRole}
        </Link>
        {queue && (
          <nav
            aria-label="Decision queue"
            className="flex items-center gap-1 text-xs text-neutral-500 tabular-nums"
          >
            <span className="mr-1">
              {queue.index + 1} of {queue.total} waiting
            </span>
            <QueueLink
              direction="previous"
              target={queue.previous}
              to={queue.previous && candidateLink(queue.previous.candidateId)}
            />
            <QueueLink
              direction="next"
              target={queue.next}
              to={queue.next && candidateLink(queue.next.candidateId)}
            />
          </nav>
        )}
      </div>
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div className="flex items-center gap-3">
          <span className="bg-secondary-150 text-primary-700 grid size-10 place-items-center rounded-full text-[13px] font-semibold tracking-wide">
            {getInitials(candidate.name)}
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-[22px] leading-7 font-semibold tracking-[-0.01em]">
                {candidate.name}
              </h1>
              <PrescreenLinkSharedBadge
                sharedPrescreenLinkType={candidate.sharedPrescreenLinkType}
                status={status}
              />
            </div>
            {currentRole && (
              <p className="text-muted-foreground mt-0.5 text-[13px]">
                {currentRole}
              </p>
            )}
          </div>
        </div>
        <div className="flex min-h-8 flex-wrap items-center gap-2">
          <CandidateDraftReview
            decision={decision}
            params={{ jobId, candidateId: candidate.candidateId }}
            pendingAction={pendingAction}
            searchSuffix={searchSuffix}
          />
          <CandidateDecisionArea
            decision={decision}
            jobId={jobId}
            candidateId={candidate.candidateId}
            firstName={candidate.firstName}
            isSelfServiceJob={candidate.isSelfServiceJob}
            onReject={onReject}
            pendingAction={pendingAction}
            status={status}
          />
        </div>
      </div>
    </header>
  );
}

function QueueLink({
  direction,
  target,
  to,
}: {
  direction: 'previous' | 'next';
  target?: { name: string };
  to?: string;
}) {
  const Icon = direction === 'previous' ? ArrowLeft : ArrowRight;
  const label = direction === 'previous' ? 'Previous' : 'Next';
  if (!target || !to) {
    return (
      <span
        aria-disabled
        className="text-input grid size-7 place-items-center rounded-md"
      >
        <Icon className="size-4" />
      </span>
    );
  }
  return (
    <Link
      aria-label={`${label} candidate: ${target.name}`}
      className="text-muted-foreground hover:bg-accent hover:text-foreground grid size-7 place-items-center rounded-md"
      title={`${label}: ${target.name}`}
      to={to}
    >
      <Icon className="size-4" />
    </Link>
  );
}
